import { useCallback, useContext, useEffect } from 'react';

import {
  getSearchRandomizedFeedState,
  SearchRandomizedFeedsContext,
} from './searchRandomizedFeeds.context';

export default function useSearchRandomizedFeed({
  feedKey,
  endpoint,
  limit,
  enabled = true,
  injectToken = false,
  optionalToken = true,
  errorMessage = 'Unable to refresh content at the moment.',
}) {
  const context = useContext(SearchRandomizedFeedsContext);
  if (!context) {
    throw new Error('useSearchRandomizedFeed must be used within SearchRandomizedFeedsProvider.');
  }

  const { feeds, loadFeed, loadMoreFeed } = context;
  const feed = getSearchRandomizedFeedState(feeds, feedKey);

  useEffect(() => {
    if (!enabled || feed.hasRequested) {
      return;
    }

    void loadFeed({
      feedKey,
      endpoint,
      limit,
      injectToken,
      optionalToken,
      errorMessage,
    });
  }, [enabled, endpoint, errorMessage, feed.hasRequested, feedKey, injectToken, optionalToken, limit, loadFeed]);

  const loadMore = useCallback(() => loadMoreFeed({
    feedKey, endpoint, limit, injectToken, optionalToken, errorMessage,
  }), [endpoint, errorMessage, feedKey, injectToken, limit, loadMoreFeed, optionalToken]);

  const retry = useCallback(() => {
    if (feed.data.length > 0) return loadMore();
    return loadFeed({ feedKey, endpoint, limit, injectToken, optionalToken, errorMessage, force: true });
  }, [endpoint, errorMessage, feed.data.length, feedKey, injectToken, limit, loadFeed, loadMore, optionalToken]);

  if (enabled && !feed.hasRequested) {
    return { ...feed, loading: true, loadMore, retry };
  }

  return { ...feed, loadMore, retry };
}
